import React, { MouseEvent, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';

import {
  VideoCameraOnIcon,
  VideoCameraOffIcon,
  VideoCameraDisableIcon,
} from '@icons';
import { ReactComponent as ScreenShareIcon } from '@public/images/camera/screen.svg';
import { ReactComponent as OnFrontAvatarIcon } from '@public/images/camera/avatar.svg';

import { useOverlayContext } from '@providers/OverlayProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useConference } from '../../../../DashboardContent/Dolby';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import { getCatchErrorMessage } from '@helpers/error';
import {
  useClientSpecificDashboardButtonDisabled,
  useClientSpecificSDScreenShareButtonDisabled,
} from '@hooks/clientSpecificUtils';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { AnalyticsType } from '@interfaces/analytics';
import BasicButton from '@components/BasicButton';
import ScrollableContainer from '@components/ScrollableContainer';
import SwipeableDrawer from '@core/dashboard/components/SwipeableDrawer';
import {
  Header,
  ModalContainer,
} from '@core/dashboard/components/PopoverModal';
import HomeBarButton from '@core/dashboard/components/HomeBarButton';
import useShareWebcam from './hooks';
import VideoComponent from '../VideoComponent';
import StyledBadge from './SyledBadge';
import { IProps } from './interfaces';
import { useStyles } from './styles';
import OVERLAYS from '@constants/overlays';
import { DashboardButtonEnum } from '@constants/dashboard';
import { COLORS } from '@constants/colors';

enum ShareOptions {
  OnFrontAvatar = 'OnFrontAvatar',
  OnScreen = 'OnScreen',
}

const ShareWebcamButton = ({
  cameraDeviceId,
  onVideoClick,
  showWarningBadge,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [sharingOption, setSharingOption] = useState<ShareOptions>(
    ShareOptions.OnFrontAvatar,
  );
  const [isStartingBroadcast, setIsStartingBroadcast] = useState(false);

  const { t: translate } = useTranslation();

  const { ueWebcamScreenName, ueWebcamMediaStreams } = useConference();

  const {
    startShareCameraToScreen,
    stopShareCameraToScreen,
    isCameraOn,
    user,
    isJoiningConference,
    isCameraPermissionGranted,
  } = useCommunications();

  const { canSpeak, trackEventAnalytics, canShowCamera, canShareCamera } =
    useMytaverseEvent();

  const isDisabledClientSpecific = useClientSpecificDashboardButtonDisabled(
    DashboardButtonEnum.CAMERA_BUTTON,
  );

  const {
    addToCurrentlyOpenOverlays,
    removeFromCurrentlyOpenOverlays,
    isOverlayCurrentlyOpen,
    hideCurrentlyOpenOverlays,
  } = useOverlayContext();

  const { currentCameraSharing } = useShareWebcam();

  const classes = useStyles();

  const isCameraButtonDisabled = useClientSpecificSDScreenShareButtonDisabled();

  useEffect(() => {
    if (canShareCamera !== canShowCamera) {
      if (canShareCamera) {
        setSharingOption(ShareOptions.OnScreen);
      } else if (canShowCamera) {
        setSharingOption(ShareOptions.OnFrontAvatar);
      }
    }
  }, [canShareCamera, canShowCamera]);

  useEffect(() => {
    // this forces the sharing option to be set back to "On Front Avatar", in case
    // the user has preselected "on Screen" and another user of the same room has
    // started to share its screen (2 users can't share their webcam on screen at the same time)

    if (
      ueWebcamMediaStreams.length &&
      !currentCameraSharing &&
      sharingOption === ShareOptions.OnScreen
    ) {
      setSharingOption(ShareOptions.OnFrontAvatar);
    }
  }, [ueWebcamMediaStreams]);

  useEffect(() => {
    // we need to reset to OnFrontAvatar if a user has selected OnScreen on a room with a ueWebcamScreenName available

    if (!ueWebcamScreenName && sharingOption === ShareOptions.OnScreen) {
      setSharingOption(ShareOptions.OnFrontAvatar);
    }
  }, [ueWebcamScreenName]);

  const closeWebcamShareDrawer = React.useCallback(() => {
    setOpen(false);
    removeFromCurrentlyOpenOverlays(OVERLAYS.WEBCAM_SHARE);
  }, [removeFromCurrentlyOpenOverlays]);

  const toggleWebcamShareDrawer = React.useCallback(() => {
    if (open) {
      closeWebcamShareDrawer();
      return;
    }

    addToCurrentlyOpenOverlays(OVERLAYS.WEBCAM_SHARE);
    setOpen(true);
  }, [open, closeWebcamShareDrawer, addToCurrentlyOpenOverlays]);

  const handleShareOnFrontAvatarClick = React.useCallback(async () => {
    setIsStartingBroadcast(true);
    try {
      await onVideoClick();
      closeWebcamShareDrawer();
    } catch (error: unknown) {
      throw Error(getCatchErrorMessage(error));
    } finally {
      setIsStartingBroadcast(false);
    }
  }, [closeWebcamShareDrawer, onVideoClick]);

  const handleShareOnScreenClick = React.useCallback(async () => {
    try {
      if (ueWebcamScreenName) {
        setIsStartingBroadcast(true);

        if (user?.bVideoOn && ueWebcamMediaStreams.length !== 0) {
          sendCustomSentryBreadcrumb({
            message: 'Stop zoom share webcam to UE',
          });
          console.log('Stop zoom share webcam to UE');
          await stopShareCameraToScreen();
          trackEventAnalytics(AnalyticsType.CameraShareFinish);
        } else {
          sendCustomSentryBreadcrumb({
            message: 'Start zoom share webcam to UE',
          });
          console.log('Start zoom share webcam to UE');
          await startShareCameraToScreen();
          trackEventAnalytics(AnalyticsType.CameraShareStart);

          closeWebcamShareDrawer();
        }
      }
    } catch (error: unknown) {
      if (canSpeak) {
        //showNotification(getDolbyNotification());
      }
      throw Error(getCatchErrorMessage(error));
    } finally {
      setIsStartingBroadcast(false);
    }
  }, [
    ueWebcamScreenName,
    ueWebcamMediaStreams,
    currentCameraSharing,
    canSpeak,
    closeWebcamShareDrawer,
    user,
    trackEventAnalytics,
  ]);

  const isScreenSharing =
    ueWebcamMediaStreams.length !== 0 && currentCameraSharing;

  const isFrontAvatarOn = isCameraOn && !!cameraDeviceId;

  const isStreaming = isScreenSharing || isFrontAvatarOn;

  const handleStartSharingVideo = React.useCallback(async () => {
    switch (sharingOption) {
      case ShareOptions.OnFrontAvatar:
        await handleShareOnFrontAvatarClick();
        break;
      case ShareOptions.OnScreen:
        await handleShareOnScreenClick();
        break;
    }
  }, [sharingOption, handleShareOnFrontAvatarClick, handleShareOnScreenClick]);

  const handleStopSharingVideo = React.useCallback(async () => {
    setIsStartingBroadcast(true);
    try {
      if (isScreenSharing) {
        sendCustomSentryBreadcrumb({
          message: 'Stop zoom share webcam to UE',
        });
        await stopShareCameraToScreen();
        trackEventAnalytics(AnalyticsType.CameraShareFinish);
      } else if (isFrontAvatarOn) {
        await onVideoClick();
      }

      closeWebcamShareDrawer();
    } catch (error: unknown) {
      if (canSpeak) {
        //showNotification(getDolbyNotification());
      }
      throw Error(getCatchErrorMessage(error));
    } finally {
      setIsStartingBroadcast(false);
    }
  }, [
    isScreenSharing,
    isFrontAvatarOn,
    stopShareCameraToScreen,
    trackEventAnalytics,
    onVideoClick,
    closeWebcamShareDrawer,
    canSpeak,
  ]);

  const handleToggleSharingVideo = React.useCallback(async () => {
    if (isStreaming) {
      await handleStopSharingVideo();
      return;
    }

    await handleStartSharingVideo();
  }, [isStreaming, handleStartSharingVideo, handleStopSharingVideo]);

  const handleSharingButtonClick = React.useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.currentTarget.blur();

      trackEventAnalytics(AnalyticsType.NavbarButtonClick, { name: 'camera' });

      if (!cameraDeviceId) {
        return;
      }

      if (isStreaming) {
        await handleStopSharingVideo();
      } else if (cameraDeviceId) {
        toggleWebcamShareDrawer();
      }
    },
    [
      cameraDeviceId,
      isStreaming,
      handleStopSharingVideo,
      trackEventAnalytics,
      toggleWebcamShareDrawer,
    ],
  );

  useEffect(() => {
    if (hideCurrentlyOpenOverlays) {
      setOpen(false);
    } else {
      if (isOverlayCurrentlyOpen(OVERLAYS.WEBCAM_SHARE)) {
        setOpen(true);
      }
    }
  }, [hideCurrentlyOpenOverlays, isOverlayCurrentlyOpen]);

  const renderButtonIcon = () => {
    if (isStartingBroadcast) {
      return <CircularProgress sx={classes.progress} />;
    }

    if (isStreaming) {
      return (
        <StyledBadge
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          variant="dot"
        >
          <VideoCameraDisableIcon />
        </StyledBadge>
      );
    }

    if (open) {
      return <VideoCameraOnIcon />;
    }

    return showWarningBadge(<VideoCameraOffIcon />);
  };

  const showDolbyShareWebcam = !!ueWebcamScreenName;
  const showBothWebcamActions = canShareCamera && canShowCamera;

  const translatedTooltip =
    cameraDeviceId || isCameraPermissionGranted
      ? isStreaming
        ? 'tooltips.turnCameraOff'
        : 'tooltips.turnCameraOn'
      : 'tooltips.warningPermissionCam';

  const showCameraOnEmphasis =
    !!cameraDeviceId &&
    !isCameraButtonDisabled &&
    !isDisabledClientSpecific &&
    (isStreaming || open) &&
    !isStartingBroadcast;

  const cameraOnButtonSx = isStreaming
    ? {
        boxShadow: `0 0 0 2px ${COLORS.ONLINE_GREEN}B3, 0 0 16px ${COLORS.ONLINE_GREEN}4D`,
        '&:hover': {
          boxShadow: `0 0 0 2px ${COLORS.ONLINE_GREEN}CC, 0 0 22px ${COLORS.ONLINE_GREEN}59`,
        },
      }
    : {
        boxShadow: '0 0 0 2px rgba(66, 114, 222, 0.78)',
        '&:hover': {
          boxShadow: '0 0 0 2px rgba(66, 114, 222, 0.92)',
        },
      };

  return (
    <>
      <HomeBarButton
        label={translate(translatedTooltip)}
        icon={renderButtonIcon()}
        isActive={open || isStreaming}
        handleClick={handleSharingButtonClick}
        disabled={
          !cameraDeviceId || isCameraButtonDisabled || isDisabledClientSpecific
        }
        isLoading={isJoiningConference}
        buttonSx={showCameraOnEmphasis ? cameraOnButtonSx : undefined}
      />
      <SwipeableDrawer
        position={{
          vertical: isMobileOnly ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        open={open}
        onClose={handleSharingButtonClick}
        onOpen={handleSharingButtonClick}
      >
        <ModalContainer
          isScrollable={false}
          header={
            <Header
              text={translate('webcamShare.title')}
              onClose={() => {
                closeWebcamShareDrawer();
              }}
              isBig
            />
          }
        >
          <ScrollableContainer
            sxProps={{
              root: classes.scrollable(
                !isJoiningConference && showDolbyShareWebcam,
              ),
            }}
          >
            {!isMobileOnly && (
              <Box sx={classes.content}>
                <VideoComponent cameraDeviceId={cameraDeviceId} />
                {!isJoiningConference &&
                showDolbyShareWebcam &&
                showBothWebcamActions ? (
                  <Box sx={classes.actions}>
                    <IconButton
                      sx={classes.button(
                        sharingOption === ShareOptions.OnFrontAvatar,
                      )}
                      onClick={() =>
                        setSharingOption(ShareOptions.OnFrontAvatar)
                      }
                      disabled={
                        !cameraDeviceId ||
                        currentCameraSharing ||
                        isScreenSharing
                      }
                      disableRipple
                    >
                      <OnFrontAvatarIcon />
                      <Typography sx={classes.buttonText}>
                        {translate('webcamShare.onFrontAvatar')}
                      </Typography>
                    </IconButton>

                    <IconButton
                      sx={classes.button(
                        sharingOption === ShareOptions.OnScreen,
                      )}
                      onClick={() => setSharingOption(ShareOptions.OnScreen)}
                      disabled={
                        (ueWebcamMediaStreams.length !== 0 &&
                          !currentCameraSharing) ||
                        isFrontAvatarOn
                      }
                      disableRipple
                    >
                      <ScreenShareIcon />
                      <Typography sx={classes.buttonText}>
                        {translate('webcamShare.onScreen')}
                      </Typography>
                    </IconButton>
                  </Box>
                ) : null}

                <BasicButton
                  onClick={handleToggleSharingVideo}
                  primary
                  disabled={isStartingBroadcast}
                  loading={isStartingBroadcast}
                >
                  {translate(
                    isStreaming
                      ? 'webcamShare.stopSharing'
                      : 'webcamShare.startSharing',
                  )}
                </BasicButton>
              </Box>
            )}
            {isMobileOnly && (
              <Box sx={classes.contentMobile}>
                <VideoComponent cameraDeviceId={cameraDeviceId} />
                <Box sx={classes.actionsWrapperMobile}>
                  {!isJoiningConference &&
                  ((showDolbyShareWebcam && canShareCamera) ||
                    canShowCamera) ? (
                    <Box sx={classes.actionsMobile}>
                      {canShowCamera && (
                        <IconButton
                          sx={classes.button(
                            sharingOption === ShareOptions.OnFrontAvatar,
                          )}
                          onClick={() =>
                            setSharingOption(ShareOptions.OnFrontAvatar)
                          }
                          disabled={
                            !cameraDeviceId ||
                            currentCameraSharing ||
                            isScreenSharing
                          }
                          disableRipple
                        >
                          <OnFrontAvatarIcon height={'14px'} />
                          <Typography
                            sx={{
                              ...classes.buttonText,
                              ...classes.buttonTextMobile,
                            }}
                          >
                            {translate('webcamShare.onFrontAvatar')}
                          </Typography>
                        </IconButton>
                      )}

                      {showDolbyShareWebcam && canShareCamera && (
                        <IconButton
                          sx={classes.button(
                            sharingOption === ShareOptions.OnScreen,
                          )}
                          onClick={() =>
                            setSharingOption(ShareOptions.OnScreen)
                          }
                          disabled={
                            (ueWebcamMediaStreams.length !== 0 &&
                              !currentCameraSharing) ||
                            isFrontAvatarOn
                          }
                          disableRipple
                        >
                          <ScreenShareIcon height={'14px'} />
                          <Typography
                            sx={{
                              ...classes.buttonText,
                              ...classes.buttonTextMobile,
                            }}
                          >
                            {translate('webcamShare.onScreen')}
                          </Typography>
                        </IconButton>
                      )}
                    </Box>
                  ) : null}
                  <BasicButton
                    onClick={handleToggleSharingVideo}
                    disabled={isStartingBroadcast}
                    loading={isStartingBroadcast}
                    primary
                    compact
                  >
                    {translate(
                      isStreaming
                        ? 'webcamShare.stopSharing'
                        : 'webcamShare.startSharing',
                    )}
                  </BasicButton>
                </Box>
              </Box>
            )}
          </ScrollableContainer>
        </ModalContainer>
      </SwipeableDrawer>
    </>
  );
};

export default ShareWebcamButton;
